import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { childrenAPI, profileAPI, authAPI } from '../services/api';
import { APP_NAME } from '../constants/config';
import './Onboarding.css';

const Onboarding = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);

  // Redirect authenticated users to dashboard
  React.useEffect(() => {
    if (isAuthenticated && currentStep === 1) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, currentStep, navigate]);
  const [formData, setFormData] = useState({
    // Parent info
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    // Child info
    childName: '',
    childAge: '',
    // Profile info
    favorites: [],
    dislikes: [],
    sensitivities: [],
    // Consent
    coppaConsent: false,
    consentTerms: false,
    consentUnderstanding: false,
  });
  const [currentInputs, setCurrentInputs] = useState({
    favorites: '',
    dislikes: '',
    sensitivities: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const totalSteps = 8;

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addToList = (field) => {
    if (currentInputs[field]?.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], currentInputs[field].trim()]
      }));
      setCurrentInputs(prev => ({ ...prev, [field]: '' }));
    }
  };

  const removeFromList = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const nextStep = () => {
    setError('');
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSignup = async () => {
    setError('');
    setLoading(true);

    try {
      // Validate
      if (!formData.email || !formData.password) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      // Validate COPPA consent
      if (!formData.coppaConsent) {
        setError('Parental consent is required to create an account');
        setLoading(false);
        return;
      }

      // Sign up with COPPA consent
      await authAPI.signup(
        formData.email,
        formData.password,
        formData.phone || null,
        formData.coppaConsent
      );

      // Login and store token
      const loginResponse = await authAPI.login(formData.email, formData.password);
      const token = loginResponse.data.access_token;

      // Store token in localStorage before calling login
      localStorage.setItem('token', token);

      // Update auth context
      login(token);

      // Small delay to ensure token is set
      await new Promise(resolve => setTimeout(resolve, 100));

      nextStep();
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChild = async () => {
    setError('');
    setLoading(true);

    try {
      if (!formData.childName || !formData.childAge) {
        setError('Please enter child name and age');
        setLoading(false);
        return;
      }

      const response = await childrenAPI.create(formData.childName, parseInt(formData.childAge));
      updateFormData('childId', response.data.id);
      nextStep();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create child profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async () => {
    setError('');
    setLoading(true);

    try {
      await profileAPI.update(formData.childId, {
        favorites: formData.favorites,
        dislikes: formData.dislikes,
        sensitivities: formData.sensitivities,
      });
      nextStep();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate(`/chat/${formData.childId}`);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="onboarding-step">
            <div className="step-icon">🤖</div>
            <h2>Welcome to {APP_NAME}</h2>
            <p className="step-description">
              A safe, engaging AI friend designed specifically for autistic children.
            </p>
            <div className="feature-list">
              <div className="feature-item">
                <span className="feature-icon">✅</span>
                <span>Safe conversations with built-in content filtering</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🎯</span>
                <span>Personalized to your child's interests</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">👨‍👩‍👧</span>
                <span>Complete parent visibility and control</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🛡️</span>
                <span>No secrets - parents see everything</span>
              </div>
            </div>
            <button className="btn-primary" onClick={nextStep}>
              Get Started
            </button>
          </div>
        );

      case 2:
        return (
          <div className="onboarding-step">
            <div className="step-icon">👤</div>
            <h2>Create Your Account</h2>
            <p className="step-description">Let's start by setting up your parent account</p>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Phone (Optional)</label>
              <input
                type="tel"
                placeholder="(123) 456-7890"
                value={formData.phone}
                onChange={(e) => updateFormData('phone', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={(e) => updateFormData('password', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={(e) => updateFormData('confirmPassword', e.target.value)}
              />
            </div>

            <div className="consent-section" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>Parental Consent (COPPA Compliance)</h3>
              <p style={{ fontSize: '14px', marginBottom: '10px', color: '#666' }}>
                As the parent or legal guardian, you are consenting to:
              </p>
              <ul style={{ fontSize: '14px', marginBottom: '15px', paddingLeft: '20px', color: '#666' }}>
                <li>Collection of your child's conversation data</li>
                <li>Storage of your child's profile information</li>
                <li>Use of this data to personalize the AI companion experience</li>
              </ul>
              <label className="consent-checkbox" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.coppaConsent}
                  onChange={(e) => updateFormData('coppaConsent', e.target.checked)}
                  style={{ marginRight: '10px', marginTop: '3px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>
                  I am the parent or legal guardian and I consent to the collection and use of my child's data as described in the Privacy Policy.
                </span>
              </label>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="button-group">
              <button className="btn-secondary" onClick={prevStep}>Back</button>
              <button
                className="btn-primary"
                onClick={handleSignup}
                disabled={loading || !formData.coppaConsent}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>

            <p className="login-link">
              Already have an account? <a href="/login">Sign in here</a>
            </p>
          </div>
        );

      case 3:
        return (
          <div className="onboarding-step">
            <div className="step-icon">👧</div>
            <h2>Tell Us About Your Child</h2>
            <p className="step-description">Basic information to get started</p>

            <div className="form-group">
              <label>Child's Name</label>
              <input
                type="text"
                placeholder="Enter your child's name"
                value={formData.childName}
                onChange={(e) => updateFormData('childName', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Age</label>
              <input
                type="number"
                min="3"
                max="18"
                placeholder="Age"
                value={formData.childAge}
                onChange={(e) => updateFormData('childAge', e.target.value)}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="button-group">
              <button className="btn-secondary" onClick={prevStep}>Back</button>
              <button className="btn-primary" onClick={handleCreateChild} disabled={loading}>
                {loading ? 'Creating Profile...' : 'Continue'}
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="onboarding-step">
            <div className="step-icon">⭐</div>
            <h2>Child's Interests & Preferences</h2>
            <p className="step-description">
              Help us personalize conversations by sharing what {formData.childName} loves, dislikes, and is sensitive about
            </p>

            <div className="form-section">
              <h3>Favorite Topics (Special Interests)</h3>
              <p className="field-description">What does {formData.childName} love to talk about?</p>
              <div className="tag-input-container">
                <input
                  type="text"
                  placeholder="Type here and press Enter or click Add"
                  value={currentInputs.favorites}
                  onChange={(e) => setCurrentInputs(prev => ({ ...prev, favorites: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && addToList('favorites')}
                  style={{ flex: 1 }}
                />
                <button onClick={() => addToList('favorites')} style={{ minWidth: '100px' }}>✓ Add</button>
              </div>
              <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 10px 0' }}>
                💡 Tip: Add one at a time by typing and pressing Enter or clicking Add
              </p>
              <div className="tag-list">
                {formData.favorites.map((item, index) => (
                  <span key={index} className="tag">
                    {item}
                    <button onClick={() => removeFromList('favorites', index)}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="form-section">
              <h3>Dislikes</h3>
              <p className="field-description">Topics to avoid in conversations</p>
              <div className="tag-input-container">
                <input
                  type="text"
                  placeholder="Type here and press Enter or click Add"
                  value={currentInputs.dislikes}
                  onChange={(e) => setCurrentInputs(prev => ({ ...prev, dislikes: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && addToList('dislikes')}
                  style={{ flex: 1 }}
                />
                <button onClick={() => addToList('dislikes')} style={{ minWidth: '100px' }}>✓ Add</button>
              </div>
              <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 10px 0' }}>
                💡 Tip: Add one at a time by typing and pressing Enter or clicking Add
              </p>
              <div className="tag-list">
                {formData.dislikes.map((item, index) => (
                  <span key={index} className="tag tag-warning">
                    {item}
                    <button onClick={() => removeFromList('dislikes', index)}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="form-section">
              <h3>Sensitivities</h3>
              <p className="field-description">Topics that may cause distress</p>
              <div className="tag-input-container">
                <input
                  type="text"
                  placeholder="Type here and press Enter or click Add"
                  value={currentInputs.sensitivities}
                  onChange={(e) => setCurrentInputs(prev => ({ ...prev, sensitivities: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && addToList('sensitivities')}
                  style={{ flex: 1 }}
                />
                <button onClick={() => addToList('sensitivities')} style={{ minWidth: '100px' }}>✓ Add</button>
              </div>
              <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 10px 0' }}>
                💡 Tip: Add one at a time by typing and pressing Enter or clicking Add
              </p>
              <div className="tag-list">
                {formData.sensitivities.map((item, index) => (
                  <span key={index} className="tag tag-danger">
                    {item}
                    <button onClick={() => removeFromList('sensitivities', index)}>×</button>
                  </span>
                ))}
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="button-group">
              <button className="btn-secondary" onClick={prevStep}>Back</button>
              <button className="btn-primary" onClick={handleCreateProfile} disabled={loading}>
                {loading ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="onboarding-step">
            <div className="step-icon">🤖</div>
            <h2>How {APP_NAME} Works</h2>
            <p className="step-description">Understanding what the AI can and cannot do</p>

            <div className="info-cards">
              <div className="info-card">
                <h3>✅ What the AI IS</h3>
                <ul>
                  <li>A friendly chat companion</li>
                  <li>Interested in your child's topics</li>
                  <li>Patient and encouraging</li>
                  <li>Here to practice conversation skills</li>
                </ul>
              </div>

              <div className="info-card">
                <h3>❌ What the AI is NOT</h3>
                <ul>
                  <li>Not a therapist or counselor</li>
                  <li>Not a medical professional</li>
                  <li>Not a replacement for human relationships</li>
                  <li>Not keeping secrets from parents</li>
                </ul>
              </div>

              <div className="info-card">
                <h3>🛡️ Safety Features</h3>
                <ul>
                  <li>Content filtering for inappropriate topics</li>
                  <li>Redirects serious concerns to parents</li>
                  <li>Never asks for personal information</li>
                  <li>Encourages real-world relationships</li>
                </ul>
              </div>
            </div>

            <div className="button-group">
              <button className="btn-secondary" onClick={prevStep}>Back</button>
              <button className="btn-primary" onClick={nextStep}>
                I Understand
              </button>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="onboarding-step">
            <div className="step-icon">🎛️</div>
            <h2>Parent Controls</h2>
            <p className="step-description">You have complete control and visibility</p>

            <div className="controls-grid">
              <div className="control-card">
                <div className="control-icon">👁️</div>
                <h3>Full Transparency</h3>
                <p>View all conversations in real-time. No secrets between the AI and your child.</p>
              </div>

              <div className="control-card">
                <div className="control-icon">⏱️</div>
                <h3>Session Limits</h3>
                <p>Set daily time limits and message limits to manage screen time.</p>
              </div>

              <div className="control-card">
                <div className="control-icon">🚫</div>
                <h3>Topic Control</h3>
                <p>Block specific topics or use whitelist mode to allow only certain discussions.</p>
              </div>

              <div className="control-card">
                <div className="control-icon">🔄</div>
                <h3>AI Toggle</h3>
                <p>Turn the AI on or off anytime with one click.</p>
              </div>

              <div className="control-card">
                <div className="control-icon">📊</div>
                <h3>Daily Summaries</h3>
                <p>Get AI-generated summaries of daily conversations and insights.</p>
              </div>

              <div className="control-card">
                <div className="control-icon">⚠️</div>
                <h3>Safety Alerts</h3>
                <p>Get notified when safety filters are triggered.</p>
              </div>
            </div>

            <div className="button-group">
              <button className="btn-secondary" onClick={prevStep}>Back</button>
              <button className="btn-primary" onClick={nextStep}>
                Continue
              </button>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="onboarding-step">
            <div className="step-icon">📋</div>
            <h2>Consent & Understanding</h2>
            <p className="step-description">Please confirm your understanding</p>

            <div className="consent-section">
              <label className="consent-checkbox">
                <input
                  type="checkbox"
                  checked={formData.consentTerms}
                  onChange={(e) => updateFormData('consentTerms', e.target.checked)}
                />
                <span>
                  I understand that this AI companion is for educational and entertainment purposes only,
                  and is not a replacement for professional medical, therapeutic, or educational services.
                </span>
              </label>

              <label className="consent-checkbox">
                <input
                  type="checkbox"
                  checked={formData.consentUnderstanding}
                  onChange={(e) => updateFormData('consentUnderstanding', e.target.checked)}
                />
                <span>
                  I understand that I can view all conversations, control topic discussions,
                  set session limits, and disable the AI at any time through the Parent Dashboard.
                </span>
              </label>
            </div>

            <div className="important-note">
              <strong>Important:</strong> If your child discusses serious topics like bullying, abuse,
              self-harm, or depression, the AI will encourage them to talk to you or another trusted adult.
              Always monitor your child's well-being and seek professional help when needed.
            </div>

            <div className="button-group">
              <button className="btn-secondary" onClick={prevStep}>Back</button>
              <button
                className="btn-primary"
                onClick={nextStep}
                disabled={!formData.consentTerms || !formData.consentUnderstanding}
              >
                I Agree
              </button>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="onboarding-step">
            <div className="step-icon">🎉</div>
            <h2>You're All Set!</h2>
            <p className="step-description">
              {formData.childName}'s profile is ready. Time to start chatting!
            </p>

            <div className="completion-card">
              <h3>What happens next?</h3>
              <ul className="next-steps">
                <li>✅ {formData.childName} can start chatting with the AI companion</li>
                <li>✅ The AI will engage with {formData.childName}'s interests</li>
                <li>✅ You can view all conversations in the Parent Dashboard</li>
                <li>✅ You can adjust controls anytime</li>
              </ul>
            </div>

            <div className="tip-card">
              <strong>💡 Tip:</strong> Check the Parent Dashboard to see conversation summaries,
              insights, and safety alerts. You can also adjust parent controls at any time.
            </div>

            <div className="button-group">
              <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </button>
              <button className="btn-primary btn-large" onClick={handleComplete}>
                Start Chatting! 💬
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
        <div className="progress-text">
          Step {currentStep} of {totalSteps}
        </div>
      </div>

      <div className="onboarding-content">
        {renderStep()}
      </div>
    </div>
  );
};

export default Onboarding;
